import { cn } from '@/lib/utils';
import { imageService } from '@/services/image';
import { getTodayOperatingHours, checkOrderAvailability, type Restaurant as BaseRestaurant, type OrderType } from '@/lib/operatingHours';

const PLACEHOLDER_IMAGE = '/images/products/background_placeholder.png';

interface MapStoreCardProps extends Partial<BaseRestaurant> {
  id: string;
  name: string;
  bestmenu: string;
  pickupInstructions: string;
  products?: Array<{
    id: string;
    name: string;
    original_price: number;
    image_url?: string;
    offers: Array<{
      id: string;
      offer_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
      discount_rate: number;
      discounted_price: number;
      is_active: boolean;
    }>;
    current_stock: number;
    is_available: boolean;
  }>;
  isOpen: boolean;
  isNew: boolean;
  storeType: 'korean' | 'chinese' | 'japanese' | 'western' | 'cafe' | 'lunchbox' | 'kimbap' | 'salad' | 'bakery' | 'buffet' | 'etc';
  imageUrl?: string;
  distance?: string;
  selectedOrderType?: OrderType;
  // Legacy support
  deadlineDiscountStartTime?: string;
  deadlineDiscountEndTime?: string;
  todayStart?: string;
  todayEnd?: string;
  onCardClick?: () => void;
  is_test_store?: boolean;
}

export function MapStoreCard({
  id,
  name,
  bestmenu,
  pickupInstructions,
  products,
  isOpen,
  isNew,
  imageUrl,
  distance,
  selectedOrderType = 'DEADLINE_DISCOUNT',
  deadlineDiscountStartTime,
  deadlineDiscountEndTime,
  todayStart,
  todayEnd,
  onCardClick,
  ...restaurantProps
}: MapStoreCardProps) {
  if (restaurantProps.is_test_store === true) {
    return null;
  }
  const isDeadlineDisabled = selectedOrderType === 'DEADLINE_DISCOUNT' && restaurantProps.deadline_discount_enabled === false;
  const isCardActive = isOpen && !isDeadlineDisabled;

  // 선택된 주문 유형에 맞는 가격/할인율 계산
  const getPriceInfo = () => {
    if (!products || products.length === 0) {
      return { discountedPrice: 0, discountRate: 0, hasValidOffer: false };
    }

    // 주문 가능한 상품 중에서 선택된 주문 유형에 맞는 오퍼 찾기
    for (const product of products) {
      if (!product.is_available || product.current_stock <= 0) continue;
      
      const activeOffer = product.offers.find(offer => 
        offer.offer_type === selectedOrderType && offer.is_active
      );
      
      if (activeOffer) {
        return {
          discountedPrice: activeOffer.discounted_price,
          discountRate: activeOffer.discount_rate,
          hasValidOffer: true
        };
      }
    }

    // 선택된 타입의 오퍼가 없으면 첫 번째 상품의 원가 표시
    const firstProduct = products.find(p => p.is_available && p.current_stock > 0);
    if (firstProduct) {
      return {
        discountedPrice: firstProduct.original_price,
        discountRate: 0,
        hasValidOffer: false
      };
    }

    return { discountedPrice: 0, discountRate: 0, hasValidOffer: false };
  };

  const { discountedPrice, discountRate, hasValidOffer } = getPriceInfo();

  const resolvedImageUrl = imageUrl && !imageUrl.startsWith('/home/') ? imageUrl : PLACEHOLDER_IMAGE;

  // 헬퍼 함수를 사용한 수령 시간 계산
  const restaurantData: BaseRestaurant = {
    id,
    name,
    bestmenu,
    is_open: isOpen,
    is_new: isNew,
    store_type: restaurantProps.store_type || 'etc',
    deadline_discount_start_time: deadlineDiscountStartTime,
    deadline_discount_end_time: deadlineDiscountEndTime,
    reservation_discount_enabled: true,
    pickup_instructions: pickupInstructions,
    created_at: restaurantProps.created_at || new Date().toISOString(),
    ...restaurantProps
  };

  const getPickupTimeText = () => {
    if (!checkOrderAvailability(restaurantData, selectedOrderType)) {
      return '주문마감';
    }

    const operatingHours = getTodayOperatingHours(restaurantData);
    
    switch (selectedOrderType) {
      case 'DEADLINE_DISCOUNT':
        if (deadlineDiscountStartTime && deadlineDiscountEndTime) {
          return `${deadlineDiscountStartTime} ~ ${deadlineDiscountEndTime}`;
        }
        return pickupInstructions;
      
      case 'RESERVATION_DISCOUNT':
        if (operatingHours.openTime && operatingHours.closeTime) {
          const [startHour, startMinute] = operatingHours.openTime.split(':').map(Number);
          const pickupStartHour = startHour + 1;
          const pickupStart = `${String(pickupStartHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`;
          return `익일 ${pickupStart} ~ ${operatingHours.closeTime}`;
        }
        return '익일 수령';
      
      case 'REGULAR_ORDER':
        return '즉시 ~ 준비 완료 후 2시간';
      
      default:
        return pickupInstructions;
    }
  };

  const pickupTimeText = getPickupTimeText();

  return (
    <div 
      className={cn("cursor-pointer bg-white rounded-lg overflow-hidden shadow-lg border border-gray-200")}
      onClick={onCardClick}
      style={{
        width: '100%',
        minWidth: '260px', // 최소 너비 설정
        maxWidth: '300px', // 480px → 300px로 축소
        opacity: isCardActive ? 1 : 0.85,
        filter: isCardActive ? 'none' : 'grayscale(40%)'
      }}
    >
      {/* 이미지 영역 */}
      <div 
        className="relative w-full overflow-hidden"
        style={{
          height: '100px' // 120px → 100px로 축소
        }}
      >
        <img 
          src={resolvedImageUrl || imageService.getRestaurantImageByName(name)}
          alt={name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center'
          }}
        />
        
        {/* 배지들 */}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {/* 주문가능/주문마감 배지 */}
          <div className={cn(
            "text-sm font-medium px-2 py-1 rounded", // text-xs → text-sm, px-1.5 → px-2, py-0.5 → py-1
            isCardActive 
              ? "bg-[#FFEFE0] text-[#222222]"
              : "bg-[#999999] text-white"
          )}>
            {isDeadlineDisabled ? '오픈대기' : (isOpen ? '주문가능' : '주문마감')}
          </div>
          
          {/* New 배지 */}
          {isNew && (
            <div className="bg-[#FF6D00] text-white text-sm font-bold px-2 py-1 rounded"> {/* text-xs → text-sm, px-1.5 → px-2, py-0.5 → py-1 */}
              New!
            </div>
          )}
        </div>

        {/* 매장명 */}
        <div className="absolute bottom-2 left-2 right-2">
          <h3 className="text-lg font-bold text-white truncate" style={{ 
            textShadow: '1px 1px 2px rgba(0,0,0,0.7)' 
          }}>
            {name}
          </h3>
        </div>
      </div>

      {/* 정보 영역 */}
      <div className="p-3 space-y-2"> {/* p-2 → p-3, space-y-1 → space-y-2 */}
        {/* 대표메뉴 */}
        <p className="text-sm text-[#222222] font-normal truncate"> {/* text-xs → text-sm */}
          {bestmenu}
        </p>

        {/* 수령가능시간 */}
        <p className="text-sm text-[#666666] truncate"> {/* text-xs → text-sm */}
          수령: {pickupTimeText}
        </p>

        {/* 하단: 거리, 할인율, 가격 */}
        <div className="flex items-center justify-between">
          {/* 왼쪽: 거리 정보 */}
          {distance && (
            <span className="text-sm text-[#666666]"> {/* text-xs → text-sm */}
              {distance}
            </span>
          )}
          
          {/* 오른쪽: 할인율과 가격 */}
          <div className="flex items-center gap-1.5 ml-auto"> {/* gap-1 → gap-1.5 */}
            {hasValidOffer && discountRate > 0 && (
              <span className="text-sm font-medium text-[#FF3B30]"> {/* text-xs → text-sm */}
                (-{discountRate}%)
              </span>
            )}
            <span className="text-base text-[#4CD964] font-semibold"> {/* text-sm → text-base */}
              {discountedPrice.toLocaleString()}원~
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
